"use strict";
/**
 * Schedule ##top pin strip helpers.
 * - Cache should always retain all top pins (even when Active/filtered board is shown).
 * - Handle modes: realm only; open Session envelope (Focus S); or T fragments under the pin.
 *
 * Vocabulary:
 * - **S** = Session envelope — lower-resolution chunk (work / study / make). Useful for
 *   billing/rollup. No commercial time-and-attendance “Shift” product in dogfood.
 * - **T** = finer Actual fragments (prepare, travel, coding, deploy, test, bugfix, …)
 *   parented under backlog / pin / I as needed.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TOP_ACTION_HANDLE_LABELS = exports.topActionSessionEnvelopePayload = exports.topActionStartPayload = exports.mergeScheduleCacheRows = exports.extractTopActionRows = exports.mergeTopActionsIntoRows = exports.pinWorkAssetTag = exports.topActionCaption = exports.scheduleRowFocusContext = exports.scheduleRowId = exports.isTopActionRow = void 0;
function isTopActionRow(row) {
    if (!row)
        return false;
    if (row.TopAction === 1 || row.TopAction === '1' || row.TopAction === true)
        return true;
    return String(row.pnAssetTag || '').trim() === '##top';
}
exports.isTopActionRow = isTopActionRow;
function scheduleRowId(row) {
    if (!row || row.ID == null)
        return 0;
    var n = Number(row.ID);
    return Number.isFinite(n) && n > 0 ? n : 0;
}
exports.scheduleRowId = scheduleRowId;
/** Account/realm for Focus context from a schedule pin or board row. */
function scheduleRowFocusContext(row) {
    if (!row)
        return { account: '', realm: '' };
    var account = String(row.pnAssetAccount != null && String(row.pnAssetAccount).trim()
        ? row.pnAssetAccount
        : row.pnAccount || '').trim();
    var realm = String(row.pnAssetRealm != null && String(row.pnAssetRealm).trim()
        ? row.pnAssetRealm
        : row.pnUserRealm || '').trim();
    return { account: account, realm: realm };
}
exports.scheduleRowFocusContext = scheduleRowFocusContext;
function topActionCaption(row) {
    var body = String((row === null || row === void 0 ? void 0 : row.pnBody) || '').trim();
    if (!body)
        return '(top)';
    var line = body.split(/\r?\n/)[0] || body;
    return line.length > 80 ? line.slice(0, 77) + '…' : line;
}
exports.topActionCaption = topActionCaption;
/**
 * Asset tag to stamp on a Focus S/T started from a pin.
 * Pin marker `##top` (and other `##…` meta tags) must not copy onto the timer row.
 */
function pinWorkAssetTag(row) {
    if (!row || row.pnAssetTag == null)
        return null;
    var tag = String(row.pnAssetTag).trim();
    if (!tag || tag.startsWith('##'))
        return null;
    return tag;
}
exports.pinWorkAssetTag = pinWorkAssetTag;
/**
 * Merge ##top pins into a board list: all tops first (stable), then non-tops by input order.
 * Dedupes by ID (tops win).
 */
function mergeTopActionsIntoRows(boardRows, topPins) {
    var tops = (topPins || []).filter(isTopActionRow);
    var board = boardRows || [];
    var seen = {};
    var out = [];
    for (var _i = 0, tops_1 = tops; _i < tops_1.length; _i++) {
        var row = tops_1[_i];
        var id = scheduleRowId(row);
        var key = id > 0 ? String(id) : "t-".concat(out.length);
        if (seen[key])
            continue;
        seen[key] = true;
        out.push(__assign(__assign({}, row), { TopAction: 1 }));
    }
    for (var _a = 0, board_1 = board; _a < board_1.length; _a++) {
        var row = board_1[_a];
        var id = scheduleRowId(row);
        var key = id > 0 ? String(id) : "b-".concat(out.length);
        if (seen[key])
            continue;
        seen[key] = true;
        if (isTopActionRow(row)) {
            out.push(__assign(__assign({}, row), { TopAction: 1 }));
        }
        else {
            out.push(__assign(__assign({}, row), { TopAction: row.TopAction != null ? row.TopAction : 0 }));
        }
    }
    return out;
}
exports.mergeTopActionsIntoRows = mergeTopActionsIntoRows;
/** Extract ##top pins from any schedule list. */
function extractTopActionRows(rows) {
    return (rows || []).filter(isTopActionRow).map(function (r) { return (__assign(__assign({}, r), { TopAction: 1 })); });
}
exports.extractTopActionRows = extractTopActionRows;
/**
 * Build next cache payload: always keep the fullest known top-pin set.
 * - New tops from the latest fetch replace by ID (fresher body/rank).
 * - Tops only present in previous cache are retained (filtered Active fetch must not drop them).
 * - Board non-top rows: prefer latest unfiltered-or-current board when provided.
 */
function mergeScheduleCacheRows(opts) {
    var prev = opts.previousRows || [];
    var next = opts.nextRows || [];
    var prevTops = extractTopActionRows(prev);
    var nextTops = extractTopActionRows(next);
    var topById = {};
    for (var _i = 0, prevTops_1 = prevTops; _i < prevTops_1.length; _i++) {
        var t = prevTops_1[_i];
        var id = scheduleRowId(t);
        if (id > 0)
            topById[String(id)] = __assign(__assign({}, t), { TopAction: 1 });
    }
    for (var _a = 0, nextTops_1 = nextTops; _a < nextTops_1.length; _a++) {
        var t = nextTops_1[_a];
        var id = scheduleRowId(t);
        if (id > 0)
            topById[String(id)] = __assign(__assign({}, t), { TopAction: 1 });
        else {
            // id-less: append under synthetic key
            topById["anon-".concat(Object.keys(topById).length)] = __assign(__assign({}, t), { TopAction: 1 });
        }
    }
    var mergedTops = Object.keys(topById).map(function (k) { return topById[k]; });
    var board;
    if (opts.replaceBoard !== false) {
        board = (next || []).filter(function (r) { return !isTopActionRow(r); });
    }
    else {
        // Keep previous board, overlay non-top from next by id
        var boardById_1 = {};
        for (var _b = 0, prev_1 = prev; _b < prev_1.length; _b++) {
            var r = prev_1[_b];
            if (isTopActionRow(r))
                continue;
            var id = scheduleRowId(r);
            if (id > 0)
                boardById_1[String(id)] = r;
        }
        for (var _c = 0, next_1 = next; _c < next_1.length; _c++) {
            var r = next_1[_c];
            if (isTopActionRow(r))
                continue;
            var id = scheduleRowId(r);
            if (id > 0)
                boardById_1[String(id)] = r;
        }
        board = Object.keys(boardById_1).map(function (k) { return boardById_1[k]; });
    }
    return mergeTopActionsIntoRows(board, mergedTops);
}
exports.mergeScheduleCacheRows = mergeScheduleCacheRows;
/**
 * Focus **T** start payload under a pin (travel / interruption / task).
 * Session envelope (S) is not a T — use startFocusS / handle mode `startSession`.
 */
function topActionStartPayload(row, mode) {
    var id = scheduleRowId(row);
    var ctx = scheduleRowFocusContext(row);
    var base = {
        parentId: id > 0 ? id : null,
        account: ctx.account,
        realm: ctx.realm,
        // Never copy pin marker ##top onto the new T
        assetTag: pinWorkAssetTag(row),
        assetTagAux: row.pnAssetTagAux != null ? String(row.pnAssetTagAux) : null,
        assetAccount: ctx.account,
        assetRealm: ctx.realm,
    };
    switch (mode) {
        case 'startTravel':
            return __assign(__assign({}, base), { taskGroup: 'Session', topicGroup: 'Travel', topicDetail: 'Start' });
        case 'startInterruption':
            return __assign(__assign({}, base), { taskGroup: 'Activity', topicGroup: 'Interruption', topicDetail: 'Start' });
        case 'startTask':
        default:
            return __assign(__assign({}, base), { taskGroup: 'Activity', topicGroup: 'Start', topicDetail: null });
    }
}
exports.topActionStartPayload = topActionStartPayload;
/**
 * Payload for opening Focus S (Session envelope) — no "Shift" topic stamp.
 * Parent is the ##top pin id so the session sits under that schedule handle.
 */
function topActionSessionEnvelopePayload(row) {
    var id = scheduleRowId(row);
    var ctx = scheduleRowFocusContext(row);
    return {
        parentId: id > 0 ? id : null,
        taskGroup: 'Session',
        topicGroup: '',
        topicDetail: 'Start',
        account: ctx.account,
        realm: ctx.realm,
        // Never copy pin marker ##top onto the new S
        assetTag: pinWorkAssetTag(row),
        assetTagAux: row.pnAssetTagAux != null ? String(row.pnAssetTagAux) : null,
        assetAccount: ctx.account,
        assetRealm: ctx.realm,
    };
}
exports.topActionSessionEnvelopePayload = topActionSessionEnvelopePayload;
exports.TOP_ACTION_HANDLE_LABELS = {
    switchRealm: 'Just switch realm',
    startSession: 'Start session (envelope S)',
    startTravel: 'Start travel',
    startInterruption: 'Start new interruption',
    startTask: 'Start task (Focus T)',
};
